/**
 * Vehicle Card labels
 * Dynamic mapping from JSON based on current language
 * Uses getLanguage() to determine which locale to use
 */

import svVehicleCard from '../locales/sv/vehicleCard.json';
import enVehicleCard from '../locales/en/vehicleCard.json';
import { getLanguage, type Language } from '@/utils/languageUtils';
import { VehicleCardStatus } from '@/types/enums';

/**
 * Helper function to get the correct locale based on language
 */
const getVehicleCardLocale = (lang?: Language) => {
  const currentLang = lang || getLanguage();
  return currentLang === 'en' ? enVehicleCard : svVehicleCard;
};

/**
 * Vehicle Card Status labels - dynamically uses current language
 */
export const vehicleCardStatusLabels = (lang?: Language): Record<string, string> => {
  const locale = getVehicleCardLocale(lang);
  return {
    ACTIVE: locale.status.active,
    INACTIVE: locale.status.inactive,
    COMPLETED: locale.status.completed,
    INCOMPLETE: locale.status.incomplete,
  };
};

/**
 * Reverse lookup: Label -> VehicleCardStatus (auto-generated from same source)
 * Uses current language to determine which labels to use
 */
export const labelToVehicleCardStatus = (lang?: Language): Record<string, VehicleCardStatus> => {
  const labels = vehicleCardStatusLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([enumKey, label]) => [label, enumKey as VehicleCardStatus])
  ) as Record<string, VehicleCardStatus>;
};

/**
 * Vehicle Card UI labels - dynamically uses current language
 */
export const vehicleCardUILabels = (lang?: Language): Record<string, string> => {
  const locale = getVehicleCardLocale(lang);
  return locale.ui;
};

/**
 * Vehicle Card Toast labels - dynamically uses current language
 */
export const vehicleCardToastLabels = (lang?: Language): Record<string, string> => {
  const locale = getVehicleCardLocale(lang);
  return locale.toasts;
};
